import React from "react";
import { getPgaBrandLogo } from "@/lib/colorScheme";
import { useColorScheme } from "@/providers/ColorSchemeProvider";

export interface PageMessageProps {
  /**
   * Main heading text
   */
  title: string;
  /**
   * Descriptive message text
   */
  message?: string;
  /**
   * Whether to show the animated loading dots
   */
  showLoadingIndicator?: boolean;
  /**
   * Whether to show the PGA logo
   */
  showLogo?: boolean;
  /**
   * Child component to render inside the message container
   */
  children?: React.ReactNode;
}

const PageMessage: React.FC<PageMessageProps> = ({
  title,
  message,
  showLoadingIndicator = true,
  showLogo = true,
  children
}) => {
  const { colorScheme } = useColorScheme();
  const { src, variant } = getPgaBrandLogo(colorScheme);
  const logoClassName = variant === "april-fools" ? "h-32 w-auto mx-auto" : "w-32 h-32 mx-auto";

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-sys-brand-surface">
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 max-w-md">
        {/* PGA Logo */}
        {showLogo && (
          <div className="mb-8">
            <img src={src} alt="PGA of America" className={logoClassName} data-testid="pga-brand-logo" />
          </div>
        )}

        {/* Content */}
        <div className="space-y-4">
          <h2 className="text-title-large-bold font-serif text-extended-pgadarknavy-brand-color">{title}</h2>
          {message && (
            <p className="text-extended-pgadarknavy-brand-color opacity-80 text-title-small leading-relaxed font-semibold">
              {message}
            </p>
          )}

          {/* Loading indicator */}
          {showLoadingIndicator && (
            <div className="flex justify-center pt-4">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-extended-pgadarknavy-brand-color-bright rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-extended-pgadarknavy-brand-color-bright rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-extended-pgadarknavy-brand-color-bright rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

export default PageMessage;
